import { GridProduct, ProductOrientation, SkinProduct } from '../types/productTypes'
import { isSkinOrientationSupported } from './productUtils'

export function getAllGridOptions(standaloneGridItemNo: string, allGrids: readonly GridProduct[]) {
  return allGrids.filter(grid => grid.standaloneGridItemNo === standaloneGridItemNo)
}

/**
 * @param skin selected skin product
 * @param selectedOrientation
 * @param mechItemNumbers list of item numbers of configured mechs (which can be null if a slot has no mech)
 * @param grids list of potential grids to select from
 */
export function findOptimalGrid(
  skin: SkinProduct,
  selectedOrientation: ProductOrientation,
  mechItemNumbers: readonly (string | null)[],
  grids: readonly GridProduct[],
): GridProduct | null {
  if (!isSkinOrientationSupported(skin, selectedOrientation)) {
    throw Error(`invalid orientation "${skin.orientationSupport ?? skin.imageOrientation}" for supported orientation "${selectedOrientation}"`)
  }

  if (grids.length === 0) {
    throw Error('findOptimalGrid expects at least one grid')
  }

  if (grids.length === 1) {
    return grids[0]
  }

  const emptyMechSlots = mechItemNumbers.filter(it => it !== null)

  /*
   *  5 scenarios for matches, in order of priority:
   *    1. exact composite match (skin + grid + mechs)
   *    2. matching mechs, no skin (grid + mechs, skin extra)
   *    3. matching mechs, different skin (grid + other skin, skin + mechs extra)
   *    4. matching grid and skin, no mechs included
   *    5. matching grid, no mechs or skin (grid + skin, mechs extra)
   */

  const doConfiguredMechsMatchGridMechs = (grid: GridProduct): boolean => {
    if (grid.includedMechItemNo !== null) {
      const matchingMechs = mechItemNumbers.filter(it => it === grid.includedMechItemNo)

      if (matchingMechs.length === grid.slotCount) {
        return true
      }
    }

    return false
  }

  // case 1: exact match
  // - grid includes skin
  // - configured mechs match grid
  // - if there are multiple matches, orientation is the deciding factor
  const exactComponentMatches = grids.filter(grid => {
    if (grid.includedSkinItemNo !== skin.itemNo) {
      return false
    }

    return (
      // no mechs configured or included with grid
      (
        grid.includedMechItemNo === null
        && emptyMechSlots.length === 0
      )
      // matching mech configuration
      || doConfiguredMechsMatchGridMechs(grid)
    )
  })

  if (exactComponentMatches.length > 0) {
    return exactComponentMatches.find(it => it.orientation === selectedOrientation)
      ?? exactComponentMatches.find(it => it.orientation === 'Both')
      ?? exactComponentMatches[0]
  }

  // case 2: match with no skin
  // - grid does not include any skin
  // - configured mechs match grid
  const mechOnlyMatch = grids.find(grid => {
    if (grid.includedSkinItemNo !== null) {
      return null
    }

    return doConfiguredMechsMatchGridMechs(grid)
  })

  if (mechOnlyMatch !== undefined) {
    return mechOnlyMatch
  }

  // case 3: match with different skin
  // - different skin included
  // - configured mechs match grid
  const mechMatchDifferentSkin = grids.find(grid => {
    return doConfiguredMechsMatchGridMechs(grid)
  })

  if (mechMatchDifferentSkin !== undefined) {
    return mechMatchDifferentSkin
  }

  // case 4: matching grid & skin
  // - skin matches
  // - grid matches
  // - no mechs included
  const noMechMatch = grids.find(grid => {
    return grid.includedSkinItemNo === skin.itemNo && grid.includedMechItemNo === null
  })

  if (noMechMatch !== undefined) {
    return noMechMatch
  }

  // case 5: matching standalone grid
  // - no skin included
  // - no mechs included
  const gridMatch = grids.find(grid =>
    grid.includedSkinItemNo === null
    && grid.includedMechItemNo === null,
  )

  if (gridMatch !== undefined) {
    return gridMatch
  }

  return null
}